import { AxiosInstance } from "axios";
import { fetchEventSource } from "@microsoft/fetch-event-source";

export interface InitResponse {
  stream_url: string;
  envelope: string;
  thread: {
    uuid: string;
    title: string | null;
    title_generated_at: string | null;
  };
  user_message: {
    uuid: string;
    role: "user";
    content: string;
    created_at: string;
  };
  assistant_message_id: string;
  expires_at: string;
}

export interface ChatMessageDto {
  uuid: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  status?: "pending" | "complete" | "error";
  completed_at?: string | null;
}

export interface ChatThreadDto {
  uuid: string;
  title: string | null;
  title_generated_at: string | null;
  last_message_at: string;
  is_archived: boolean;
  message_count: number;
}

export interface ThreadsPage {
  threads: ChatThreadDto[];
  next: string | null;
  previous: string | null;
}

export interface ReplayPage {
  messages: ChatMessageDto[];
  next: string | null;
  previous: string | null;
}

export type ChatStreamEvent =
  | { type: "token"; text: string }
  | { type: "complete"; message: string }
  | { type: "error"; code: string; message: string };

export interface StreamHandle {
  close(): void;
}

export async function initChatMessage(
  api: AxiosInstance,
  args: {
    threadUuid?: string; // omit to create a new thread
    content: string; // 1..8000 chars
    maxTokens?: number; // optional, capped at 32000
    modelOverride?: string | null;
    clientMessageId?: string; // for offline-resync dedup; auto-gen if omitted
  },
): Promise<InitResponse> {
  const url = args.threadUuid
    ? `/mobile/chat/threads/${args.threadUuid}/messages/init`
    : `/mobile/chat/threads/init`;

  const resp = await api.post(url, {
    content: args.content,
    max_tokens: args.maxTokens,
    model_override: args.modelOverride ?? null,
    client_message_id: args.clientMessageId,
  });

  return resp.data.result;
}

const parseSseMessage = (event: any): ChatStreamEvent | null => {
  if (!event.data) return null;

  const rawType = (event.type || event.event || "message").toString();

  try {
    const payload = JSON.parse(event.data as string);

    if (rawType === "token") {
      return { type: "token", text: payload.text ?? "" };
    }

    if (rawType === "complete" || rawType === "done") {
      return {
        type: "complete",
        message: payload.message ?? payload.finish_reason ?? "stop",
      };
    }

    if (rawType === "error") {
      return {
        type: "error",
        code: payload.code ?? "unknown",
        message: payload.message ?? "Stream error occurred",
      };
    }

    if (payload.text) {
      return { type: "token", text: payload.text };
    }

    if (payload.message || payload.finish_reason) {
      return {
        type: "complete",
        message: payload.message ?? payload.finish_reason ?? "stop",
      };
    }
  } catch (error) {
    console.error("Failed to parse SSE payload:", error, event.data);
  }

  return null;
};

export function openChatStream(
  init: InitResponse,
  onEvent: (event: ChatStreamEvent) => void,
  onError: (error: any) => void,
): StreamHandle {
  const controller = new AbortController();

  void fetchEventSource(init.stream_url, {
    method: "GET",
    headers: {
      Authorization: `PASETO ${init.envelope}`,
      Accept: "text/event-stream",
    },
    signal: controller.signal,
    onmessage(event) {
      const parsed = parseSseMessage(event);

      if (parsed) {
        onEvent(parsed);
      }
    },
    async onopen(response) {
      if (!response.ok) {
        console.error(
          `Failed to open stream: ${response.status} ${response.statusText}`,
        );
        throw new Error(
          `Failed to open stream: ${response.status} ${response.statusText}`,
        );
      }
    },
    onclose() {
      onEvent({ type: "complete", message: "stream_closed" });
    },
    onerror(error) {
      onError(error);
      return;
    },
  }).catch((error) => {
    if (!controller.signal.aborted) {
      onError(error);
    }
  });

  return {
    close: () => controller.abort(),
  };
}

export async function replayThread(
  api: AxiosInstance,
  threadUuid: string,
  cursor?: string,
): Promise<ReplayPage> {
  const params = cursor ? { cursor } : {};
  const resp = await api.get(`/mobile/chat/threads/${threadUuid}/messages/`, {
    params,
  });
  return resp.data.result;
}

export async function listThreads(
  api: AxiosInstance,
  args: { archived?: boolean; cursor?: string } = {},
): Promise<ThreadsPage> {
  const params: Record<string, any> = {};
  if (args.archived !== undefined) params.archived = args.archived;
  if (args.cursor) params.cursor = args.cursor;

  const resp = await api.get("/mobile/chat/threads/", { params });
  return resp.data.result;
}

export async function searchThreads(
  api: AxiosInstance,
  query: string,
): Promise<ChatThreadDto[]> {
  const resp = await api.get("/mobile/chat/threads/", {
    params: { q: query },
  });
  return resp.data.result.threads;
}

export async function updateThread(
  api: AxiosInstance,
  threadUuid: string,
  updates: { title?: string; is_archived?: boolean },
): Promise<ChatThreadDto> {
  const resp = await api.patch(`/mobile/chat/threads/${threadUuid}/`, updates);
  return resp.data.result;
}

export async function deleteThread(
  api: AxiosInstance,
  threadUuid: string,
): Promise<void> {
  await api.delete(`/mobile/chat/threads/${threadUuid}/`);
}
